import {
    Alert,
    Box,
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Typography,
    useTheme,
} from "@mui/material";

import FileDownloadOutlinedIcon from
    "@mui/icons-material/FileDownloadOutlined";

import EmojiEventsOutlinedIcon from
    "@mui/icons-material/EmojiEventsOutlined";
import TrendingUpOutlinedIcon from
    "@mui/icons-material/TrendingUpOutlined";
import { useEffect, useMemo, useState } from "react";

import * as XLSX from "xlsx";

import { tokens } from "../../theme";
import {
    djangoApi,
} from "../../utils/axios/axios.utils";

const moneyFormatter = new Intl.NumberFormat(
    "pt-BR",
    {
        style: "currency",
        currency: "BRL",
        minimumFractionDigits: 2,
    }
);

const numberFormatter = new Intl.NumberFormat(
    "pt-BR"
);

const ABC_COLORS = {
    up_to_300: "#7897c9",
    from_300_to_500: "#f3a33b",
    from_500_to_1000: "#75b798",
    from_1000_to_1500: "#9d7ac7",
    above_1500: "#db6d75",
};

const TABLE_COLUMNS = [
    {
        key: "nome",
        label: "Nome",
        align: "left",
    },
    {
        key: "total_kits",
        label: "Número total de maletas",
    },
    {
        key: "sales_last_12_months",
        label: "Total em vendas últimos 12 meses",
    },
    {
        key: "sales_last_3_months",
        label: "Total em vendas últimos 3 meses",
    },
    {
        key:
            "average_sales_per_kit_last_12_months",
        label:
            "Média por maleta últimos 12 meses",
    },
    {
        key:
            "average_sales_per_kit_last_3_months",
        label:
            "Média por maleta últimos 3 meses",
    },
    {
        key:
            "sales_percentage_of_kit_value_last_12_months",
        label:
            "% vendido sobre maletas — 12 meses",
    },
    {
        key:
            "sales_percentage_of_kit_value_last_3_months",
        label:
            "% vendido sobre maletas — 3 meses",
    },
];

const toNumber = (value) => {
    const parsedValue = Number(value);

    return Number.isFinite(parsedValue)
        ? parsedValue
        : 0;
};

const formatCurrency = (value) => {
    return moneyFormatter.format(
        toNumber(value)
    );
};

const formatPercentage = (value) => {
    return `${toNumber(value).toLocaleString(
        "pt-BR",
        {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
        }
    )}%`;
};

const RankingSellers = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [rankingData, setRankingData] =
        useState(null);

    const [isLoading, setIsLoading] =
        useState(true);

    const [error, setError] = useState("");

    const [chartPeriod, setChartPeriod] =
        useState(3);

    const [orderBy, setOrderBy] = useState(
        "sales_last_3_months"
    );

    const [orderDirection, setOrderDirection] =
        useState("desc");

    useEffect(() => {
        const getRanking = async () => {
            setIsLoading(true);
            setError("");

            try {
                const response = await djangoApi.get(
                    "products/kits_active/get_sellers_ranking/",
                    {
                        headers: {
                            Authorization:
                                `Token ${process.env.REACT_APP_DJANGO_TOKEN}`,
                            "Content-Type":
                                "application/json",
                        },
                    }
                );

                setRankingData(
                    response?.data?.data || null
                );
            } catch (requestError) {
                console.error(
                    "Erro ao consultar ranking:",
                    requestError
                );

                setError(
                    requestError?.response?.data?.msg ||
                    "Não foi possível carregar o ranking."
                );
            } finally {
                setIsLoading(false);
            }
        };

        getRanking();
    }, []);

    const sellers = useMemo(() => {
        const sellersData =
            rankingData?.sellers || [];

        return [...sellersData].sort(
            (firstSeller, secondSeller) => {
                const firstValue =
                    firstSeller?.[orderBy];

                const secondValue =
                    secondSeller?.[orderBy];

                if (orderBy === "nome") {
                    const comparison =
                        String(firstValue || "")
                            .localeCompare(
                                String(secondValue || ""),
                                "pt-BR"
                            );

                    return orderDirection === "asc"
                        ? comparison
                        : comparison * -1;
                }

                const comparison =
                    toNumber(firstValue) -
                    toNumber(secondValue);

                return orderDirection === "asc"
                    ? comparison
                    : comparison * -1;
            }
        );
    }, [
        rankingData,
        orderBy,
        orderDirection,
    ]);

    const distributionKey =
        chartPeriod === 12
            ? "last_12_months"
            : "last_3_months";

    const distribution = useMemo(() => {
        const distributionData =
            rankingData?.distribution?.[
            distributionKey
            ] || [];

        return distributionData.map((item) => ({
            ...item,
            color:
                ABC_COLORS[item.key] ||
                colors.blueAccent[400],
        }));
    }, [
        colors.blueAccent,
        distributionKey,
        rankingData,
    ]);

    const pieBackground = useMemo(() => {
        const totalPercentage = distribution.reduce(
            (total, item) => {
                return (
                    total +
                    toNumber(
                        item.percentage_of_revenue
                    )
                );
            },
            0
        );

        if (!totalPercentage) {
            return colors.primary[300];
        }

        let currentPosition = 0;

        const slices = distribution
            .filter((item) => {
                return toNumber(
                    item.percentage_of_revenue
                ) > 0;
            })
            .map((item) => {
                const nextPosition =
                    currentPosition +
                    toNumber(
                        item.percentage_of_revenue
                    );

                const slice =
                    `${item.color} ${currentPosition}% ` +
                    `${nextPosition}%`;

                currentPosition = nextPosition;

                return slice;
            });

        return `conic-gradient(${slices.join(
            ", "
        )})`;
    }, [
        colors.primary,
        distribution,
    ]);

    const handleSort = (columnKey) => {
        if (columnKey === orderBy) {
            setOrderDirection((currentDirection) => {
                return currentDirection === "asc"
                    ? "desc"
                    : "asc";
            });

            return;
        }

        setOrderBy(columnKey);

        setOrderDirection(
            columnKey === "nome"
                ? "asc"
                : "desc"
        );
    };


    const handleExportExcel = () => {
        const rankingRows = sellers.map(
            (seller, index) => ({
                Posição: index + 1,
                Revendedora: seller.nome || "-",
                "Número total de maletas": toNumber(
                    seller.total_kits
                ),
                "Total em vendas últimos 12 meses": toNumber(
                    seller.sales_last_12_months
                ),
                "Total em vendas últimos 3 meses": toNumber(
                    seller.sales_last_3_months
                ),
                "Média por maleta últimos 12 meses": toNumber(
                    seller.average_sales_per_kit_last_12_months
                ),
                "Média por maleta últimos 3 meses": toNumber(
                    seller.average_sales_per_kit_last_3_months
                ),
                "% de vendas últimos 12 meses":
                    toNumber(
                        seller.sales_percentage_of_kit_value_last_12_months
                    ) / 100,
                "% de vendas últimos 3 meses":
                    toNumber(
                        seller.sales_percentage_of_kit_value_last_3_months
                    ) / 100,
            })
        );

        const distributionRows = distribution.map(
            (item) => ({
                Faixa: item.label,
                Revendedoras: toNumber(
                    item.sellers_count
                ),
                Faturamento: toNumber(item.revenue),
                "Participação no faturamento":
                    toNumber(
                        item.percentage_of_revenue
                    ) / 100,
            })
        );

        const workbook = XLSX.utils.book_new();

        const rankingSheet = XLSX.utils.json_to_sheet(
            rankingRows
        );

        const distributionSheet =
            XLSX.utils.json_to_sheet(
                distributionRows
            );

        rankingSheet["!cols"] = [
            { wch: 10 },
            { wch: 32 },
            { wch: 18 },
            { wch: 24 },
            { wch: 23 },
            { wch: 24 },
            { wch: 23 },
            { wch: 20 },
            { wch: 19 },
        ];

        distributionSheet["!cols"] = [
            { wch: 28 },
            { wch: 16 },
            { wch: 18 },
            { wch: 26 },
        ];

        for (
            let row = 2;
            row <= rankingRows.length + 1;
            row += 1
        ) {
            ["D", "E", "F", "G"].forEach((column) => {
                if (rankingSheet[`${column}${row}`]) {
                    rankingSheet[
                        `${column}${row}`
                    ].z = '"R$" #,##0.00';
                }
            });

            ["H", "I"].forEach((column) => {
                if (rankingSheet[`${column}${row}`]) {
                    rankingSheet[
                        `${column}${row}`
                    ].z = "0.0%";
                }
            });
        }

        for (
            let row = 2;
            row <= distributionRows.length + 1;
            row += 1
        ) {
            if (distributionSheet[`C${row}`]) {
                distributionSheet[
                    `C${row}`
                ].z = '"R$" #,##0.00';
            }

            if (distributionSheet[`D${row}`]) {
                distributionSheet[
                    `D${row}`
                ].z = "0.0%";
            }
        }

        XLSX.utils.book_append_sheet(
            workbook,
            rankingSheet,
            "Ranking"
        );

        XLSX.utils.book_append_sheet(
            workbook,
            distributionSheet,
            `ABC - ${chartPeriod} meses`
        );

        const date = new Date()
            .toISOString()
            .slice(0, 10);

        XLSX.writeFile(
            workbook,
            `ranking-revendedoras-${date}.xlsx`
        );
    };

    const renderCellValue = (
        seller,
        columnKey
    ) => {
        if (columnKey === "nome") {
            return seller.nome || "-";
        }

        if (columnKey === "total_kits") {
            return numberFormatter.format(
                toNumber(seller.total_kits)
            );
        }

        if (columnKey.includes("percentage")) {
            return formatPercentage(
                seller[columnKey]
            );
        }

        return formatCurrency(
            seller[columnKey]
        );
    };

    if (isLoading) {
        return (
            <Box
                sx={{
                    minHeight: "70vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box m="20px" mt="60px">
            <Stack
                direction={{
                    xs: "column",
                    md: "row",
                }}
                justifyContent="space-between"
                alignItems={{
                    xs: "flex-start",
                    md: "center",
                }}
                spacing={2}
                mb={3}
            >
                <Box>
                    <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                    >
                        <EmojiEventsOutlinedIcon
                            sx={{
                                color: colors.yellow[500],
                                fontSize: 32,
                            }}
                        />

                        <Typography
                            variant="h3"
                            fontWeight={700}
                            color={colors.grey[100]}
                        >
                            Ranking de Revendedoras
                        </Typography>
                    </Stack>

                    <Typography
                        variant="body1"
                        color={colors.grey[300]}
                        mt={0.5}
                    >
                        Análise consolidada das vendas
                        realizadas por maletas fechadas.
                    </Typography>
                </Box>

                <Button
                    variant="contained"
                    startIcon={<FileDownloadOutlinedIcon />}
                    disabled={!sellers.length}
                    onClick={handleExportExcel}
                    sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 700,
                        backgroundColor: colors.greenAccent[600],
                        "&:hover": {
                            backgroundColor: colors.greenAccent[500],
                        },
                    }}
                >
                    Exportar Excel
                </Button>
                <Box
                    sx={{
                        px: 2,
                        py: 1,
                        borderRadius: 2,
                        backgroundColor: colors.primary[400],
                        border: `1px solid ${colors.primary[300]}`,
                    }}
                >
                    <Typography
                        variant="caption"
                        color={colors.grey[300]}
                    >
                        Revendedoras ativas
                    </Typography>

                    <Typography
                        variant="h4"
                        fontWeight={700}
                        color={colors.grey[100]}
                    >
                        {numberFormatter.format(
                            toNumber(
                                rankingData?.totals?.sellers
                            )
                        )}
                    </Typography>
                </Box>
            </Stack>

            {error ? (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            ) : null}

            <Paper
                elevation={0}
                sx={{
                    p: {
                        xs: 2,
                        md: 3,
                    },
                    mb: 3,
                    borderRadius: 3,
                    backgroundColor: colors.primary[400],
                    border: `1px solid ${colors.primary[300]}`,
                }}
            >
                <Stack
                    direction={{
                        xs: "column",
                        lg: "row",
                    }}
                    spacing={4}
                    alignItems="center"
                >
                    <Box
                        sx={{
                            flex: 1,
                            width: "100%",
                        }}
                    >
                        <Stack
                            direction={{
                                xs: "column",
                                sm: "row",
                            }}
                            justifyContent="space-between"
                            alignItems={{
                                xs: "flex-start",
                                sm: "center",
                            }}
                            spacing={2}
                            mb={3}
                        >
                            <Box>
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                >
                                    <TrendingUpOutlinedIcon
                                        sx={{
                                            color:
                                                colors.blueAccent[300],
                                        }}
                                    />

                                    <Typography
                                        variant="h5"
                                        fontWeight={700}
                                        color={colors.grey[100]}
                                    >
                                        Participação do faturamento
                                    </Typography>
                                </Stack>

                                <Typography
                                    variant="body2"
                                    color={colors.grey[300]}
                                    mt={0.5}
                                >
                                    Faixas calculadas pela média de vendas por
                                    maleta de cada revendedora.
                                </Typography>
                            </Box>

                            <FormControl
                                size="small"
                                sx={{ minWidth: 210 }}
                            >
                                <InputLabel>
                                    Período analisado
                                </InputLabel>

                                <Select
                                    label="Período analisado"
                                    value={chartPeriod}
                                    onChange={(event) => {
                                        setChartPeriod(
                                            event.target.value
                                        );
                                    }}
                                >
                                    <MenuItem value={3}>
                                        Últimos 3 meses
                                    </MenuItem>

                                    <MenuItem value={12}>
                                        Últimos 12 meses
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </Stack>

                        <Stack
                            direction={{
                                xs: "column",
                                sm: "row",
                            }}
                            spacing={3}
                            alignItems="center"
                        >
                            <Box
                                sx={{
                                    width: 210,
                                    height: 210,
                                    flexShrink: 0,
                                    borderRadius: "50%",
                                    background: pieBackground,
                                    position: "relative",
                                }}
                            >
                                <Box
                                    sx={{
                                        position: "absolute",
                                        inset: 42,
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        textAlign: "center",
                                        borderRadius: "50%",
                                        backgroundColor:
                                            colors.primary[400],
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        color={colors.grey[300]}
                                    >
                                        Faturamento
                                    </Typography>

                                    <Typography
                                        variant="h6"
                                        fontWeight={700}
                                        color={colors.grey[100]}
                                    >
                                        {formatCurrency(
                                            chartPeriod === 12
                                                ? rankingData?.totals
                                                    ?.sales_last_12_months
                                                : rankingData?.totals
                                                    ?.sales_last_3_months
                                        )}
                                    </Typography>
                                </Box>
                            </Box>

                            <Stack spacing={1.2} width="100%">
                                {distribution.map((item) => (
                                    <Stack
                                        key={item.key}
                                        direction="row"
                                        alignItems="center"
                                        spacing={1}
                                        sx={{
                                            width: "100%",
                                            maxWidth: 440,
                                        }}
                                    >
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            alignItems="center"
                                            sx={{
                                                flexShrink: 0,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 12,
                                                    height: 12,
                                                    borderRadius: "50%",
                                                    backgroundColor: item.color,
                                                }}
                                            />

                                            <Typography
                                                variant="body2"
                                                color={colors.grey[100]}
                                            >
                                                {item.label}
                                            </Typography>
                                        </Stack>

                                        <Box
                                            sx={{
                                                flex: 1,
                                                minWidth: 16,
                                                borderBottom: `1px dotted ${colors.grey[700]}`,
                                                transform: "translateY(2px)",
                                            }}
                                        />

                                        <Typography
                                            variant="body2"
                                            fontWeight={700}
                                            color={colors.grey[100]}
                                            sx={{
                                                minWidth: 48,
                                                textAlign: "right",
                                            }}
                                        >
                                            {formatPercentage(
                                                item.percentage_of_revenue
                                            )}
                                        </Typography>
                                    </Stack>
                                ))}
                            </Stack>
                        </Stack>
                    </Box>
                </Stack>
            </Paper>

            <Paper
                elevation={0}
                sx={{
                    overflow: "hidden",
                    borderRadius: 3,
                    backgroundColor: colors.primary[400],
                    border: `1px solid ${colors.primary[300]}`,
                }}
            >
                <TableContainer>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                {TABLE_COLUMNS.map((column) => (
                                    <TableCell
                                        key={column.key}
                                        align={column.align || "right"}
                                        sx={{
                                            minWidth:
                                                column.key === "nome"
                                                    ? 210
                                                    : 175,
                                            backgroundColor:
                                                colors.blueAccent[800],
                                            color: colors.grey[100],
                                            fontWeight: 700,
                                            whiteSpace: "normal",
                                            lineHeight: 1.25,
                                        }}
                                    >
                                        <TableSortLabel
                                            active={
                                                orderBy === column.key
                                            }
                                            direction={
                                                orderBy === column.key
                                                    ? orderDirection
                                                    : "asc"
                                            }
                                            onClick={() => {
                                                handleSort(column.key);
                                            }}
                                            sx={{
                                                color: `${colors.grey[100]} !important`,
                                                "& .MuiTableSortLabel-icon": {
                                                    color: `${colors.grey[100]} !important`,
                                                },
                                            }}
                                        >
                                            {column.label}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {sellers.map((seller) => (
                                <TableRow
                                    key={seller.id}
                                    hover
                                    sx={{
                                        "&:last-child td": {
                                            borderBottom: 0,
                                        },
                                    }}
                                >
                                    {TABLE_COLUMNS.map((column) => (
                                        <TableCell
                                            key={column.key}
                                            align={
                                                column.align || "right"
                                            }
                                            sx={{
                                                color:
                                                    column.key === "nome"
                                                        ? colors.grey[100]
                                                        : colors.grey[200],
                                                fontWeight:
                                                    column.key === "nome"
                                                        ? 700
                                                        : 400,
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {renderCellValue(
                                                seller,
                                                column.key
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}

                            {!sellers.length ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={
                                            TABLE_COLUMNS.length
                                        }
                                        align="center"
                                        sx={{
                                            py: 5,
                                            color: colors.grey[300],
                                        }}
                                    >
                                        Nenhuma revendedora encontrada.
                                    </TableCell>
                                </TableRow>
                            ) : null}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Box>
    );
};

export default RankingSellers;